import React, { useEffect, useMemo, useState } from 'react';

const DEFAULT_COMMON_CATEGORIES = new Set([
  'Entertainment',
  'Grocery',
  'Restaurants & Dining',
  'Transportation',
]);

function TransactionList({ transactions }) {
  const [filterCard, setFilterCard] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [people, setPeople] = useState([]);
  const [newPerson, setNewPerson] = useState('');
  const [assignments, setAssignments] = useState({});

  const cards = [...new Set(transactions.map(t => t.card))];
  const categories = [...new Set(transactions.map(t => t.category))];

  const txKey = (txn) => String(txn._id ?? `${txn.date}|${txn.card}|${txn.description}|${txn.amount}`);

  const parseTxnDate = (dateStr) => {
    if (!dateStr) return 0;
    const parts = String(dateStr).trim().split(/[/.-]/);
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD
        return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10)).getTime();
      }
      if (parts[2].length === 4 || parts[2].length === 2) {
        // DD/MM/YYYY or DD/MM/YY
        const yr = parts[2].length === 2 ? 2000 + parseInt(parts[2], 10) : parseInt(parts[2], 10);
        return new Date(yr, parseInt(parts[1], 10) - 1, parseInt(parts[0], 10)).getTime();
      }
    }
    return new Date(dateStr).getTime() || 0;
  };

  const isBillPayment = (txn) => {
    // Exclude only bill payment rows (e.g. BBPS Payment Received).
    // Refunds, cashbacks and returns are legitimate credits against the current
    // statement and should remain in the running total.
    const desc = (txn.description || '').toLowerCase();
    return txn.amount < 0 && (desc.includes('bbps') || desc.includes('payment received'));
  };

  const shouldIgnoreForDue = isBillPayment;

  useEffect(() => {
    // Ensure Common exists for default shared categories.
    setPeople(prev => (prev.includes('Common') ? prev : [...prev, 'Common']));

    // Assign "Common" only when not already assigned by user.
    setAssignments(prev => {
      const next = { ...prev };
      transactions.forEach((txn) => {
        if (!DEFAULT_COMMON_CATEGORIES.has(txn.category)) return;
        const key = txKey(txn);
        if (!next[key]) {
          next[key] = 'Common';
        }
      });
      return next;
    });
  }, [transactions]);

  let filtered = transactions.filter(t => {
    if (filterCard && t.card !== filterCard) return false;
    if (filterCategory && t.category !== filterCategory) return false;
    return true;
  });

  filtered.sort((a, b) => {
    if (sortBy === 'amount') return a.amount - b.amount;
    if (sortBy === '-amount') return b.amount - a.amount;
    if (sortBy === 'date') return parseTxnDate(a.date) - parseTxnDate(b.date);
    if (sortBy === '-date') return parseTxnDate(b.date) - parseTxnDate(a.date);
    // Default: oldest first
    return parseTxnDate(a.date) - parseTxnDate(b.date);
  });

  const addPerson = () => {
    const trimmed = newPerson.trim();
    if (!trimmed) return;
    if (people.some(p => p.toLowerCase() === trimmed.toLowerCase())) {
      setNewPerson('');
      return;
    }
    setPeople(prev => [...prev, trimmed]);
    setNewPerson('');
  };

  const handleAssign = (txn, person) => {
    const key = txKey(txn);
    setAssignments(prev => ({ ...prev, [key]: person }));
  };

  const statementSummary = useMemo(() => {
    const txnSumVal = filtered.reduce((sum, txn) => {
      if (shouldIgnoreForDue(txn)) return sum;
      return sum + txn.amount;
    }, 0);

    const seenStatements = new Set();
    const cardTxnSums = {};
    const cardHeaderSums = {};
    const cardPriorUnpaid = {};
    let headerTotalSum = 0;
    let totalPriorUnpaid = 0;
    let hasPriorUnpaid = false;
    let hasHeaderTotals = false;

    filtered.forEach(txn => {
      if (!shouldIgnoreForDue(txn)) {
        cardTxnSums[txn.card] = (cardTxnSums[txn.card] || 0) + txn.amount;
      }
      const sid = txn._statement_id;
      const tot = txn._statement_total_outstanding;
      const prior = txn._statement_prior_unpaid;
      if (sid && tot != null) {
        if (!seenStatements.has(sid)) {
          seenStatements.add(sid);
          cardHeaderSums[txn.card] = parseFloat(tot);
          headerTotalSum += parseFloat(tot);
          hasHeaderTotals = true;
          if (prior != null && parseFloat(prior) > 0) {
            cardPriorUnpaid[txn.card] = parseFloat(prior);
            totalPriorUnpaid += parseFloat(prior);
            hasPriorUnpaid = true;
          }
        }
      }
    });

    const displayedNetTotal = hasPriorUnpaid
      ? (headerTotalSum - totalPriorUnpaid)
      : txnSumVal;

    const carryoverGap = hasPriorUnpaid
      ? totalPriorUnpaid
      : (hasHeaderTotals ? (headerTotalSum - txnSumVal) : 0);

    const gapCards = hasPriorUnpaid
      ? Object.keys(cardPriorUnpaid).filter(c => cardPriorUnpaid[c] > 0)
      : Object.keys(cardHeaderSums).filter(c => {
          const diff = (cardHeaderSums[c] || 0) - (cardTxnSums[c] || 0);
          return diff > 5.0;
        });

    const gapCardsLabel = gapCards.length > 0
      ? gapCards.map(c => c.split(' ')[0]).join(' & ')
      : 'Statement Adjustments';

    const showGap = hasHeaderTotals && Math.abs(carryoverGap) > 1;

    return {
      txnSum: txnSumVal,
      headerTotalSum,
      totalPriorUnpaid,
      hasPriorUnpaid,
      hasHeaderTotals,
      displayedNetTotal,
      carryoverGap,
      gapCardsLabel,
      showGap
    };
  }, [filtered, shouldIgnoreForDue]);

  const personAnalysis = useMemo(() => {
    const rawDirectTotals = {};
    people.forEach(person => {
      rawDirectTotals[person] = 0;
    });

    filtered.forEach((txn) => {
      if (shouldIgnoreForDue(txn)) return;
      const assigned = assignments[txKey(txn)];
      if (!assigned) return;
      rawDirectTotals[assigned] = (rawDirectTotals[assigned] || 0) + txn.amount;
    });

    const rawTotalAssigned = Object.values(rawDirectTotals).reduce((sum, val) => sum + val, 0);
    const targetNetTotal = statementSummary.displayedNetTotal;

    const scaleFactor = (rawTotalAssigned > 0 && targetNetTotal > 0)
      ? (targetNetTotal / rawTotalAssigned)
      : 1;

    const directTotals = {};
    people.forEach(person => {
      directTotals[person] = (rawDirectTotals[person] || 0) * scaleFactor;
    });

    const commonTotal = directTotals['Common'] || 0;
    const manualPeople = people.filter(p => p !== 'Common');
    const manualCount = manualPeople.length;
    const commonShare = manualCount > 0 ? (commonTotal / manualCount) : 0;

    const finalTotals = {};
    people.forEach(person => {
      if (person === 'Common') {
        finalTotals[person] = manualCount > 0 ? 0 : commonTotal;
      } else {
        finalTotals[person] = (directTotals[person] || 0) + commonShare;
      }
    });

    const totalAssigned = targetNetTotal > 0 ? targetNetTotal : Object.values(finalTotals).reduce((sum, val) => sum + val, 0);

    return {
      directTotals,
      commonTotal,
      manualPeople,
      manualCount,
      commonShare,
      finalTotals,
      totalAssigned
    };
  }, [people, filtered, assignments, shouldIgnoreForDue, statementSummary.displayedNetTotal]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="summary-section">
      <h2>Transaction Details</h2>

      <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #eee', borderRadius: '8px', background: '#fafafa' }}>
        <label style={{ display: 'block', marginBottom: '8px', color: '#555', fontWeight: '600' }}>
          Add Person for Allocation
        </label>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={newPerson}
            onChange={(e) => setNewPerson(e.target.value)}
            placeholder="Enter person name"
            style={{ minWidth: '220px' }}
          />
          <button type="button" onClick={addPerson} style={{ width: 'auto', padding: '10px 18px' }}>
            Add Person
          </button>
        </div>
        <p style={{ marginTop: '10px', color: '#666' }}>
          Available: <strong>{people.join(', ')}</strong>
        </p>
      </div>

      <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', color: '#555', fontWeight: '500' }}>
            Filter by Card
          </label>
          <select
            value={filterCard}
            onChange={(e) => setFilterCard(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
          >
            <option value="">All Cards</option>
            {cards.map(card => (
              <option key={card} value={card}>{card}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '5px', color: '#555', fontWeight: '500' }}>
            Filter by Category
          </label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '5px', color: '#555', fontWeight: '500' }}>
            Sort by
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
          >
            <option value="-amount">Amount (High to Low)</option>
            <option value="amount">Amount (Low to High)</option>
            <option value="-date">Date (Newest)</option>
            <option value="date">Date (Oldest)</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '40px', textAlign: 'center' }}>#</th>
              <th>Date</th>
              <th>Card</th>
              <th>Description</th>
              <th>Type</th>
              <th>Category</th>
              <th>Person</th>
              <th className="amount-cell">Amount</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map((txn, idx) => {
                const key = txKey(txn);
                const assigned = assignments[key] || '';
                const isCredit = txn.amount < 0;
                return (
                  <tr key={key}>
                    <td style={{ textAlign: 'center', color: '#999', fontSize: '0.85em' }}>{idx + 1}</td>
                    <td>{txn.date}</td>
                    <td><strong>{txn.card}</strong></td>
                    <td style={{ fontSize: '0.9em', color: '#444' }}>
                      {txn.description.substring(0, 50)}
                      {txn.description.length > 50 ? '...' : ''}
                    </td>
                    <td>
                      <span style={{
                        background: isCredit ? '#e8f7ee' : '#fff4e5',
                        color: isCredit ? '#1f7a3f' : '#a15c00',
                        padding: '4px 8px',
                        borderRadius: '3px',
                        fontSize: '0.85em',
                        fontWeight: '600'
                      }}>
                        {isCredit ? 'Credit' : 'Debit'}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        background: '#e7f3ff',
                        color: '#0066cc',
                        padding: '4px 8px',
                        borderRadius: '3px',
                        fontSize: '0.85em',
                        fontWeight: '500'
                      }}>
                        {txn.category}
                      </span>
                    </td>
                    <td>
                      <select
                        value={assigned}
                        onChange={(e) => handleAssign(txn, e.target.value)}
                        style={{
                          width: '140px',
                          padding: '6px',
                          borderRadius: '5px',
                          border: assigned ? '1px solid #ddd' : '2px solid #e53e3e',
                          outline: assigned ? 'none' : 'none',
                          boxShadow: assigned ? 'none' : '0 0 0 1px #e53e3e22',
                          transition: 'border 0.2s, box-shadow 0.2s',
                        }}
                      >
                        <option value="">Unassigned</option>
                        {people.map((person) => (
                          <option key={person} value={person}>{person}</option>
                        ))}
                      </select>
                    </td>
                    <td className="amount-cell">{formatCurrency(Math.abs(txn.amount))}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
                  No transactions match the selected filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{
        marginTop: '15px',
        padding: '16px 20px',
        borderRadius: '8px',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        border: '1px solid #cbd5e0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: statementSummary.showGap ? '10px' : '0' }}>
          <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '1.05em' }}>
            Transaction List Net Total ({filtered.length} Line Items):
          </span>
          <span style={{ fontWeight: '700', fontSize: '1.3em', color: '#2b6cb0' }}>
            {formatCurrency(statementSummary.displayedNetTotal)}
          </span>
        </div>

        {statementSummary.showGap && (
          <div style={{
            marginTop: '10px',
            paddingTop: '10px',
            borderTop: '1px dashed #a0aec0',
            fontSize: '0.9em',
            color: '#4a5568'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Statement Header Total Payment Due (across uploaded PDFs):</span>
              <strong>{formatCurrency(statementSummary.headerTotalSum)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#742a2a' }}>
              <span>Prior Unpaid Balance Carryover / Header Adjustments ({statementSummary.gapCardsLabel}):</span>
              <strong>+ {formatCurrency(statementSummary.carryoverGap)}</strong>
            </div>
          </div>
        )}
      </div>


      <div style={{ marginTop: '24px' }}>
        <h3 style={{ marginBottom: '12px' }}>Live Amount Due by Person</h3>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Person</th>
                <th className="amount-cell">Direct Assigned</th>
                <th className="amount-cell">Common Split</th>
                <th className="amount-cell">Final Total Due</th>
              </tr>
            </thead>
            <tbody>
              {people.map((person) => {
                const isCommon = person === 'Common';
                const direct = personAnalysis.directTotals[person] || 0;
                const final = personAnalysis.finalTotals[person] || 0;
                const hasManual = personAnalysis.manualCount > 0;

                return (
                  <tr key={person} style={{ backgroundColor: isCommon ? '#fcfcfc' : 'transparent' }}>
                    <td>
                      <strong>{person}</strong>
                      {isCommon && hasManual && (
                        <span style={{ fontSize: '0.85em', color: '#718096', marginLeft: '8px' }}>
                          (Distributed equally across {personAnalysis.manualCount} {personAnalysis.manualCount === 1 ? 'person' : 'people'})
                        </span>
                      )}
                    </td>
                    <td className="amount-cell">{formatCurrency(direct)}</td>
                    <td className="amount-cell" style={{ color: isCommon ? '#718096' : '#2b6cb0' }}>
                      {isCommon ? (
                        hasManual ? `-${formatCurrency(direct)}` : '—'
                      ) : (
                        hasManual ? `+${formatCurrency(personAnalysis.commonShare)}` : '—'
                      )}
                    </td>
                    <td className="amount-cell" style={{ fontWeight: 'bold', color: isCommon && hasManual ? '#a0aec0' : '#2d3748' }}>
                      {formatCurrency(final)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: 'bold', borderTop: '2px solid #cbd5e0', backgroundColor: '#f7fafc' }}>
                <td>TOTAL DUE</td>
                <td className="amount-cell">{formatCurrency(personAnalysis.totalAssigned)}</td>
                <td className="amount-cell">—</td>
                <td className="amount-cell" style={{ color: '#2b6cb0', fontSize: '1.05em' }}>
                  {formatCurrency(personAnalysis.totalAssigned)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p style={{ marginTop: '8px', color: '#777', fontSize: '0.9em' }}>
          Expenses assigned under <strong>Common</strong> ({formatCurrency(personAnalysis.commonTotal)}) are split equally ({personAnalysis.manualCount > 0 ? formatCurrency(personAnalysis.commonShare) + ' per person' : 'once people are added'}) among manually added name entries and added to their final total due.
        </p>
      </div>

      <p style={{ marginTop: '15px', color: '#666' }}>
        Showing <strong>{filtered.length}</strong> of <strong>{transactions.length}</strong> transactions
      </p>
    </div>
  );
}

export default TransactionList;
